// src/pages/CourseDetail.js

import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { courses } from "../data/courses";

const CourseDetail = () => {
  const { courseId } = useParams();
  const course = courses[courseId];

  const [openWeek, setOpenWeek] = useState(0);

  if (!course) {
    return (
      <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <h2 className="heading-secondary mb-md">Course Not Found</h2>
          <p className="text-large">The course you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  const curriculumMap = {
    "equity-research": [
      {
        week: "Week 1",
        tag: "Foundation",
        sessions: [
          "Introduction to Financial Modeling",
          "Understanding Revenue Model and Assumptions",
          "Capex & Depreciation Schedule",
        ],
      },
      {
        week: "Week 2",
        tag: "Core Skills",
        sessions: [
          "Working Capital & Debt Schedule",
          "Linking 3 Financial Statements",
          "DCF & Relative Valuation",
        ],
      },
      {
        week: "Weeks 3–5",
        tag: "Live Project",
        sessions: [
          "Live Project Assignment",
          "Mentorship Sessions",
          "Project Submission & Review",
        ],
      },
    ],
    "fixed-income": [
      {
        week: "Weekend 1",
        tag: "Foundation",
        sessions: [
          "Introduction to Fixed Income Securities",
          "YTM, TVM, and Bond Valuation",
        ],
      },
      {
        week: "Weekend 2",
        tag: "Advanced Concepts",
        sessions: [
          "Duration, Convexity & Portfolio Sensitivity",
          "Credit Risk Metrics (LGD, PD, CVA)",
          "Altman Z-Score Modeling",
        ],
      },
      {
        week: "Weeks 3–4",
        tag: "Live Project",
        sessions: [
          "Live Project on Bond Valuation",
          "Credit Risk Assessment",
          "Investment Thesis Preparation",
        ],
      },
    ],
    "quantitative-finance": [
      {
        week: "Week 1",
        tag: "Python Basics",
        sessions: [
          "Introduction to Python",
          "Pandas, NumPy, Yfinance Libraries",
        ],
      },
      {
        week: "Week 2",
        tag: "Portfolio Theory",
        sessions: [
          "Portfolio Management Theory",
          "Mean-Variance Optimization",
          "Monte Carlo Simulation",
        ],
      },
      {
        week: "Week 3",
        tag: "Risk Management",
        sessions: [
          "Risk Management & VaR",
          "Efficient Frontier Creation",
        ],
      },
      {
        week: "Weeks 4–5",
        tag: "Live Project",
        sessions: [
          "Final Portfolio Project",
          "Risk Analysis Implementation",
          "Professional Report Writing",
        ],
      },
    ],
  };

  const heroImage =
    courseId === "equity-research"
      ? "https://images.unsplash.com/photo-1551288049-bebda4e38f71"
      : courseId === "fixed-income"
      ? "https://images.unsplash.com/photo-1526628953301-3e589a6a8b74"
      : "https://images.pexels.com/photos/5716001/pexels-photo-5716001.jpeg";

  const curriculum = curriculumMap[courseId] || [];

  const courseAccent = {
    "equity-research": "#2ECC9A",
    "fixed-income": "#2ECC9A",
    "quantitative-finance": "#2ECC9A",
  }[courseId] || "#2ECC9A";

  return (
    <div className="cd-page">

      {/* ── HERO ── */}
      <section className="cd-hero">
        <div className="cd-hero-inner">

          {/* Left */}
          <div className="cd-hero-content">
            <span className="cd-price-badge">₹{course.price.toLocaleString()}</span>
            <h1 className="cd-hero-title">{course.title}</h1>
            <p className="cd-hero-desc">{course.description}</p>

            <div className="cd-meta-row">
              <div className="cd-meta-pill">
                <span className="cd-meta-label">Duration</span>
                <span className="cd-meta-value">{course.duration}</span>
              </div>
              <div className="cd-meta-sep" />
              <div className="cd-meta-pill">
                <span className="cd-meta-label">Format</span>
                <span className="cd-meta-value">{course.format}</span>
              </div>
            </div>

            <div className="cd-hero-actions">
              <a
                href="https://rzp.io/rzp/cGHJ7TZk"
                target="_blank"
                rel="noopener noreferrer"
                className="cd-btn-primary"
              >
                Enroll Now →
              </a>
              <a href="#curriculum" className="cd-btn-outline">
                View Curriculum
              </a>
            </div>
          </div>

          {/* Right */}
          <div className="cd-hero-img-wrap">
            <div className="cd-img-glow" />
            <img
              src={heroImage}
              alt={course.title}
              className="cd-hero-img"
            />
          </div>
        </div>
      </section>

      {/* ── FEATURES + OUTCOMES ── */}
      <section className="cd-features-section">
        <div className="cd-container">
          <div className="cd-features-grid">

            <div className="cd-feat-card">
              <div className="cd-feat-header">
                <span className="cd-feat-icon">⚡</span>
                <h3 className="cd-feat-title">Key Features</h3>
              </div>
              <ul className="cd-feat-list">
                {course.features.map((f, i) => (
                  <li key={i}>
                    <span className="cd-check">✓</span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>

            <div className="cd-feat-card cd-feat-card-alt">
              <div className="cd-feat-header">
                <span className="cd-feat-icon">🎯</span>
                <h3 className="cd-feat-title">Learning Outcomes</h3>
              </div>
              <ul className="cd-feat-list">
                {course.outcomes.map((o, i) => (
                  <li key={i}>
                    <span className="cd-check">✓</span>
                    {o}
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* ── CURRICULUM ── */}
      <section className="cd-curriculum" id="curriculum">
        <div className="cd-container">
          <div className="cd-section-header">
            <span className="cd-eyebrow">What You'll Learn</span>
            <h2 className="cd-section-title">Curriculum</h2>
            <p className="cd-section-sub">
              A structured, week-by-week breakdown of everything covered in this programme.
            </p>
          </div>

          <div className="cd-accordion">
            {curriculum.map((week, i) => (
              <div
                key={i}
                className={`cd-acc-item ${openWeek === i ? "cd-acc-open" : ""}`}
              >
                <button
                  className="cd-acc-trigger"
                  onClick={() => setOpenWeek(openWeek === i ? null : i)}
                >
                  <div className="cd-acc-left">
                    <span className="cd-acc-step">0{i + 1}</span>
                    <div>
                      <span className="cd-acc-week">{week.week}</span>
                      <span className="cd-acc-tag">{week.tag}</span>
                    </div>
                  </div>
                  <span className="cd-acc-chevron">{openWeek === i ? "−" : "+"}</span>
                </button>

                {openWeek === i && (
                  <div className="cd-acc-body">
                    {week.sessions.map((s, j) => (
                      <div key={j} className="cd-session-row">
                        <span className="cd-session-num">{j + 1}</span>
                        <span className="cd-session-text">{s}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="cd-cta">
        <div className="cd-cta-inner">
          <div className="cd-cta-glow" />
          <span className="cd-eyebrow cd-eyebrow-light">Ready to Begin?</span>
          <h2 className="cd-cta-title">Start Your Finance Journey</h2>
          <p className="cd-cta-sub">
            Join {course.title} and build the skills that open doors in finance.
          </p>
          <a
            href="https://rzp.io/rzp/cGHJ7TZk"
            target="_blank"
            rel="noopener noreferrer"
            className="cd-cta-btn"
          >
            Enroll Now — ₹{course.price.toLocaleString()}
          </a>
        </div>
      </section>

      {/* ── STYLES ── */}
      <style jsx>{`
        /* ── Tokens ── */
        .cd-page {
          --green: #2ECC9A;
          --green-dark: #1aab7d;
          --navy: #0f1f2e;
          --surface: #f4f7f6;
          --text-primary: #0f1f2e;
          --text-secondary: #4a5e6d;
          --text-muted: #7a8c99;
          --radius-lg: 1.25rem;
          --radius-md: 0.875rem;
          --shadow-card: 0 4px 24px rgba(15,31,46,0.07);
          --shadow-hover: 0 12px 40px rgba(15,31,46,0.13);
        }

        /* ── HERO ── */
        .cd-hero {
          background: linear-gradient(135deg, #f0f6f4 0%, #e8f4ef 60%, #d8eee6 100%);
          padding: 6rem 2rem 5rem;
          overflow: hidden;
        }
        .cd-hero-inner {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 4rem;
          align-items: center;
        }
        .cd-price-badge {
          display: inline-block;
          background: var(--green);
          color: #fff;
          font-weight: 700;
          font-size: 1.1rem;
          padding: 0.45rem 1.2rem;
          border-radius: 999px;
          margin-bottom: 1.4rem;
          box-shadow: 0 4px 14px rgba(46,204,154,0.35);
        }
        .cd-hero-title {
          font-size: clamp(2rem, 3.5vw, 3rem);
          font-weight: 700;
          color: var(--text-primary);
          line-height: 1.15;
          margin: 0 0 1.1rem;
        }
        .cd-hero-desc {
          font-size: 1.05rem;
          color: var(--text-secondary);
          line-height: 1.75;
          margin-bottom: 2rem;
        }
        .cd-meta-row {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          margin-bottom: 2.5rem;
          padding: 1.2rem 1.6rem;
          background: #fff;
          border-radius: var(--radius-md);
          border: 1px solid rgba(46,204,154,0.2);
          width: fit-content;
        }
        .cd-meta-pill { display: flex; flex-direction: column; gap: 0.2rem; }
        .cd-meta-label {
          font-size: 0.72rem;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--text-muted);
          font-weight: 600;
        }
        .cd-meta-value {
          font-size: 0.95rem;
          font-weight: 600;
          color: var(--text-primary);
        }
        .cd-meta-sep {
          width: 1px;
          height: 36px;
          background: rgba(15,31,46,0.1);
        }
        .cd-hero-actions { display: flex; gap: 1rem; flex-wrap: wrap; }
        .cd-btn-primary {
          display: inline-block;
          background: var(--green);
          color: #fff;
          font-weight: 600;
          font-size: 0.95rem;
          padding: 0.85rem 2rem;
          border-radius: 999px;
          text-decoration: none;
          transition: background 0.2s, transform 0.2s, box-shadow 0.2s;
          box-shadow: 0 4px 16px rgba(46,204,154,0.35);
        }
        .cd-btn-primary:hover {
          background: var(--green-dark);
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(46,204,154,0.45);
        }
        .cd-btn-outline {
          display: inline-block;
          background: transparent;
          color: var(--text-primary);
          font-weight: 600;
          font-size: 0.95rem;
          padding: 0.85rem 2rem;
          border-radius: 999px;
          border: 2px solid rgba(15,31,46,0.2);
          text-decoration: none;
          transition: border-color 0.2s, color 0.2s;
        }
        .cd-btn-outline:hover {
          border-color: var(--green);
          color: var(--green-dark);
        }

        /* Hero image */
        .cd-hero-img-wrap {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .cd-img-glow {
          position: absolute;
          width: 70%;
          height: 70%;
          background: radial-gradient(circle, rgba(46,204,154,0.2), transparent 70%);
          border-radius: 50%;
          z-index: 0;
        }
        .cd-hero-img {
          width: 100%;
          max-height: 420px;
          object-fit: cover;
          border-radius: var(--radius-lg);
          box-shadow: 0 24px 60px rgba(15,31,46,0.18);
          position: relative;
          z-index: 1;
        }

        /* ── SHARED ── */
        .cd-container {
          max-width: 1100px;
          margin: 0 auto;
          padding: 0 2rem;
        }
        .cd-eyebrow {
          display: block;
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--green-dark);
          margin-bottom: 0.6rem;
        }
        .cd-eyebrow-light { color: rgba(46,204,154,0.9); }
        .cd-section-header {
          text-align: center;
          margin-bottom: 3rem;
        }
        .cd-section-title {
          font-size: clamp(1.6rem, 2.5vw, 2.2rem);
          font-weight: 700;
          color: var(--text-primary);
          margin: 0 0 0.75rem;
        }
        .cd-section-sub {
          font-size: 1rem;
          color: var(--text-secondary);
          max-width: 480px;
          margin: 0 auto;
          line-height: 1.7;
        }

        /* ── FEATURES ── */
        .cd-features-section {
          padding: 5rem 2rem;
          background: #fff;
        }
        .cd-features-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
        }
        .cd-feat-card {
          background: var(--surface);
          border-radius: var(--radius-lg);
          padding: 2.5rem 2.25rem;
          border: 1px solid rgba(15,31,46,0.07);
          transition: box-shadow 0.25s, border-color 0.25s, transform 0.25s;
        }
        .cd-feat-card:hover {
          box-shadow: var(--shadow-hover);
          border-color: rgba(46,204,154,0.25);
          transform: translateY(-4px);
        }
        .cd-feat-card-alt {
          background: linear-gradient(135deg, #f0faf6, #e8f4ef);
        }
        .cd-feat-header {
          display: flex;
          align-items: center;
          gap: 0.9rem;
          margin-bottom: 1.75rem;
        }
        .cd-feat-icon { font-size: 1.6rem; }
        .cd-feat-title {
          font-size: 1.15rem;
          font-weight: 700;
          color: var(--text-primary);
          margin: 0;
        }
        .cd-feat-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 0.85rem;
        }
        .cd-feat-list li {
          display: flex;
          align-items: flex-start;
          gap: 0.7rem;
          font-size: 0.9rem;
          color: var(--text-primary);
          line-height: 1.5;
        }
        .cd-check {
          color: var(--green);
          font-weight: 700;
          flex-shrink: 0;
          margin-top: 1px;
        }

        /* ── CURRICULUM ── */
        .cd-curriculum {
          padding: 6rem 2rem;
          background: var(--surface);
        }
        .cd-accordion {
          max-width: 860px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .cd-acc-item {
          background: #fff;
          border-radius: var(--radius-md);
          border: 1px solid rgba(15,31,46,0.08);
          overflow: hidden;
          transition: box-shadow 0.2s, border-color 0.2s;
        }
        .cd-acc-item.cd-acc-open {
          border-color: rgba(46,204,154,0.3);
          box-shadow: 0 4px 20px rgba(46,204,154,0.1);
        }
        .cd-acc-trigger {
          width: 100%;
          background: none;
          border: none;
          padding: 1.4rem 1.75rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
          text-align: left;
        }
        .cd-acc-left {
          display: flex;
          align-items: center;
          gap: 1.25rem;
        }
        .cd-acc-step {
          width: 42px;
          height: 42px;
          border-radius: 50%;
          background: var(--green);
          color: #fff;
          font-weight: 700;
          font-size: 0.85rem;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .cd-acc-open .cd-acc-step {
          background: var(--navy);
        }
        .cd-acc-week {
          display: block;
          font-weight: 700;
          font-size: 1rem;
          color: var(--text-primary);
        }
        .cd-acc-tag {
          display: inline-block;
          font-size: 0.72rem;
          background: rgba(46,204,154,0.12);
          color: var(--green-dark);
          border-radius: 999px;
          padding: 0.15rem 0.65rem;
          font-weight: 600;
          margin-top: 0.2rem;
          letter-spacing: 0.04em;
        }
        .cd-acc-chevron {
          font-size: 1.4rem;
          color: var(--green);
          font-weight: 300;
          line-height: 1;
          flex-shrink: 0;
        }
        .cd-acc-body {
          padding: 0 1.75rem 1.75rem;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          border-top: 1px solid rgba(15,31,46,0.06);
          margin-top: 0;
          padding-top: 1.25rem;
        }
        .cd-session-row {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.7rem 1rem;
          background: var(--surface);
          border-radius: 0.6rem;
        }
        .cd-session-num {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: rgba(46,204,154,0.15);
          color: var(--green-dark);
          font-weight: 700;
          font-size: 0.8rem;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .cd-session-text {
          font-size: 0.9rem;
          color: var(--text-primary);
        }

        /* ── CTA ── */
        .cd-cta {
          background: var(--navy);
          padding: 7rem 2rem;
          position: relative;
          overflow: hidden;
        }
        .cd-cta-glow {
          position: absolute;
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(46,204,154,0.1) 0%, transparent 70%);
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          pointer-events: none;
        }
        .cd-cta-inner {
          max-width: 620px;
          margin: 0 auto;
          text-align: center;
          position: relative;
          z-index: 1;
        }
        .cd-cta-title {
          font-size: clamp(1.8rem, 3vw, 2.5rem);
          font-weight: 700;
          color: #fff;
          margin: 0.5rem 0 1rem;
        }
        .cd-cta-sub {
          font-size: 1rem;
          color: rgba(255,255,255,0.6);
          line-height: 1.75;
          margin-bottom: 2.5rem;
        }
        .cd-cta-btn {
          display: inline-block;
          background: var(--green);
          color: #fff;
          font-weight: 600;
          font-size: 1rem;
          padding: 1rem 2.4rem;
          border-radius: 999px;
          text-decoration: none;
          transition: background 0.2s, transform 0.2s, box-shadow 0.2s;
          box-shadow: 0 4px 20px rgba(46,204,154,0.4);
        }
        .cd-cta-btn:hover {
          background: var(--green-dark);
          transform: translateY(-2px);
          box-shadow: 0 8px 32px rgba(46,204,154,0.5);
        }

        /* ── Responsive ── */
        @media (max-width: 900px) {
          .cd-hero-inner {
            grid-template-columns: 1fr;
            gap: 2.5rem;
          }
          .cd-hero-img-wrap { order: -1; }
          .cd-features-grid { grid-template-columns: 1fr; }
        }
        @media (max-width: 560px) {
          .cd-meta-row { flex-direction: column; align-items: flex-start; gap: 1rem; }
          .cd-meta-sep { display: none; }
        }
      `}</style>

    </div>
  );
};

export default CourseDetail;